import React, { useEffect, useRef, useState } from 'react';
import {
  getSupabaseRuntimeErrorMessage,
  getSupabaseStoragePublicUrl,
  hasSupabaseConfig,
  supabase,
} from '../../api/supabaseClient';
import { buildPublicAssetUrl } from '../../config/clientEnv';

type SearchType = 'food' | 'news' | 'recruitment';

export interface SearchBarProps {
  placeholder?: string;
  onSearchSelect?: (item: any | null) => void;
  onQueryChange?: (query: string) => void;
  searchType?: SearchType;
  localItems?: any[];
  searchKeys?: string[];
  maxResults?: number;
  getItemLabel?: (item: any) => string;
  renderItem?: (item: any) => React.ReactNode;
}

const DEFAULT_PLACEHOLDERS: Record<SearchType, string> = {
  food: 'Search products or nutrition facts',
  news: 'Search news by title or date',
  recruitment: 'Search recruitment notices',
};

const FOOD_IMAGE_FALLBACK_URL = buildPublicAssetUrl('thumbnail.png');

const getFoodImageUrl = (imageUrl: string) => {
  if (!imageUrl) {
    return '';
  }

  return imageUrl.startsWith('http')
    ? imageUrl
    : getSupabaseStoragePublicUrl('food-images', imageUrl) ||
        FOOD_IMAGE_FALLBACK_URL;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  onSearchSelect,
  onQueryChange,
  searchType = 'food',
  localItems,
  searchKeys = [],
  maxResults = 5,
  getItemLabel,
  renderItem,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    onQueryChange?.(query);
  }, [onQueryChange, query]);

  useEffect(() => {
    const fetchResults = async () => {
      const trimmedQuery = query.trim();

      if (!trimmedQuery) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        if (localItems) {
          const normalizedQuery = trimmedQuery.toLowerCase();
          const filteredItems = localItems.filter((item) =>
            searchKeys.some((key) => {
              const value = item?.[key];
              return (
                typeof value === 'string' &&
                value.toLowerCase().includes(normalizedQuery)
              );
            })
          );

          setResults(filteredItems.slice(0, maxResults));
          return;
        }

        if (searchType === 'food') {
          if (!hasSupabaseConfig) {
            setResults([]);
            return;
          }

          const { data, error } = await supabase
            .from('food_items')
            .select('*')
            .ilike('name', `%${trimmedQuery}%`)
            .limit(maxResults);

          if (error) {
            throw error;
          }

          setResults(data || []);
          return;
        }

        setResults([]);
      } catch (error) {
        console.error(
          '[SearchBar] search failed:',
          getSupabaseRuntimeErrorMessage(error)
        );
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = window.setTimeout(fetchResults, 250);
    return () => window.clearTimeout(debounceTimer);
  }, [localItems, maxResults, query, searchKeys, searchType]);

  const resolveItemLabel = (item: any) => {
    if (getItemLabel) {
      return getItemLabel(item);
    }

    return item?.name || item?.title || '';
  };

  const handleSelect = (item: any) => {
    const nextQuery = resolveItemLabel(item);
    setQuery(nextQuery);
    setShowDropdown(false);
    onSearchSelect?.(item);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setShowDropdown(Boolean(value.trim()));

    if (!value.trim()) {
      onSearchSelect?.(null);
    }
  };

  const renderDefaultItem = (item: any) => {
    if (searchType === 'food') {
      const imageUrl = getFoodImageUrl(item.image_url);

      return (
        <div className="flex items-center gap-4">
          {imageUrl ? (
            <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-900 overflow-hidden flex-shrink-0">
              <img
                src={imageUrl}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : null}
          <div>
            <div className="text-sm font-bold text-on-surface dark:text-white mb-1">
              {item.name}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold text-primary dark:text-blue-400">
                {typeof item.price === 'number'
                  ? `${item.price.toLocaleString()} KRW`
                  : ''}
              </span>
              <span className="text-[10px] text-on-surface-variant dark:text-slate-400 bg-surface-container-low dark:bg-slate-700 px-1.5 py-0.5 rounded">
                {item.calories} kcal
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (searchType === 'news') {
      return (
        <div>
          <div className="text-sm font-bold text-on-surface dark:text-white line-clamp-2">
            {item.title}
          </div>
          <div className="mt-1 text-xs text-on-surface-variant dark:text-slate-400">
            {item.pubDate}
          </div>
        </div>
      );
    }

    return (
      <div className="text-sm font-bold text-on-surface dark:text-white">
        {resolveItemLabel(item)}
      </div>
    );
  };

  return (
    <div ref={searchRef} className="relative group w-full">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 sm:pl-5">
        <span
          className="material-symbols-outlined text-outline-variant group-focus-within:text-primary transition-colors"
          translate="no"
        >
          search
        </span>
      </div>
      <input
        className="block w-full rounded-full border border-outline-variant/30 bg-surface-container-lowest py-3 pl-12 pr-14 text-sm font-medium text-on-surface shadow-sm transition-all placeholder:text-outline focus:border-primary focus:ring-4 focus:ring-primary/10 dark:bg-slate-800 dark:text-white sm:py-4 sm:pl-14 sm:pr-16"
        placeholder={placeholder || DEFAULT_PLACEHOLDERS[searchType]}
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={() => {
          if (query.trim()) {
            setShowDropdown(true);
          }
        }}
      />
      <button
        type="button"
        className="absolute inset-y-1.5 right-1.5 flex items-center justify-center rounded-full bg-primary px-3 text-on-primary transition-colors hover:bg-on-primary-fixed-variant sm:inset-y-2 sm:right-2 sm:px-4"
        onClick={() => {
          if (results.length > 0) {
            handleSelect(results[0]);
          }
        }}
        aria-label="Run search"
      >
        <span className="material-symbols-outlined text-[20px]" translate="no">
          arrow_forward
        </span>
      </button>

      {showDropdown && query.trim() ? (
        <div className="absolute top-full left-0 right-0 mt-3 bg-white dark:bg-slate-800 rounded-xl shadow-[0_12px_40px_rgba(27,28,28,0.06)] border border-slate-100 dark:border-slate-700 overflow-hidden z-50">
          {loading ? (
            <div className="p-4 text-center text-sm text-slate-500">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <ul>
              {results.map((item, index) => (
                <li
                  key={item?.id || item?.link || index}
                  className="p-3 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-0 transition-colors"
                  onClick={() => handleSelect(item)}
                >
                  {renderItem ? renderItem(item) : renderDefaultItem(item)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-sm text-slate-500">
              No results found.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
